import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import DataTable, { type TableColumn } from 'react-data-table-component';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  type PaperProps,
} from '@mui/material';
import {
  RiDeleteBinLine,
  RiEyeLine,
  RiUploadCloudLine,
  RiUploadLine,
  RiCheckFill,
  RiCloseLine,
  RiFullscreenLine,
  RiFullscreenExitLine,
  RiInformationLine,
} from '@remixicon/react';
import { useTranslation } from 'react-i18next';
import { sileo } from 'sileo';
import { customAlert } from '@/shared/components';
import SearchInputMUI from '@/shared/components/SearchInput/SearchInputMUI';
import TableEmptyState from '@/shared/components/EmptyStates/TableEmptyState';
import { format, parseISO } from 'date-fns';
import { CUSTOM_STYLES } from '@/shared/libs/ReactDataTable';
import { useBuzonJudicial, useBuzonRealtimeUpdates, useInstanciasDisponibles, useNucsPage } from '@/shared/hooks/useBuzonJudicial.hook';
import { useHabilitarBuzonJudicial, filtrarCategoriasPorCaso, type ResumenActivacionAgrupada, type ModoHabilitacion } from '@/shared/hooks/useHabilitarBuzonJudicial.hook';
import { LISTA_OPCIONES_PAGINACION, storage } from '@/shared/utils/Const.utils';
import type { BuzonNucPreference, BuzonDeactivatePayload, BuzonReactivatePayload, BuzonCategoriaTribunal } from '@/shared/types/interfaces/BuzonJudicial.interface';
import { BuzonNucStatusEnum } from '@/shared/types/interfaces/BuzonJudicial.interface';
import { useUserData } from '@/shared/hooks/useUserData.hook';
import { fileToBase64 } from '@/modules/login/utils/fileConversion.utils';
import { es } from 'date-fns/locale/es';
import BuzonEstadoChip from './BuzonEstadoChip';
import BuzonAccionesCell from './BuzonAccionesCell';
import HabilitarBuzonModal from './HabilitarBuzonModal';
import HabilitarModoToggle from './HabilitarModoToggle';
import BuzonAyudaPopover from './BuzonAyudaPopover';
import CategoriaTribunalSelect from './HabilitarBuzonModal/CategoriaTribunalSelect';
import { useUserPermissions } from '@/shared/hooks/useUserPermissions';
import type { PerfilEntidad } from '@/shared/types/interfaces/user.interface';
import { StorageKeys } from '@/shared/types/enums/Storage.enum';

interface DeactivateModalState {
  open: boolean;
  mode: 'single' | 'multiple';
  action: 'deactivate' | 'reactivate';
  idCasoBuzon?: number;
  idsCasoBuzon?: number[];
  /** idCaso del caso único (mode='single') — necesario para GET /instancias-disponibles al rehabilitar por instancia */
  idCaso?: number;
  nucLabel?: string;
  /** Categorías actualmente autorizadas del caso — solo se define para action='deactivate' + mode='single'
   *  cuando el caso está Habilitado por instancia (punto 3: elegir cuáles deshabilitar). `undefined` en
   *  cualquier otro escenario preserva el comportamiento actual (deshabilitación total del caso). */
  categoriasAutorizadas?: BuzonCategoriaTribunal[];
}

function DraggablePaperComponent(props: PaperProps) {
  const { isMaximized, nodeRef, ...other } = props as any;

  return (
    <Draggable
      key={isMaximized ? 'maximized' : 'draggable'}
      handle="#draggable-dialog-inactivar-title"
      cancel={'[class*="MuiDialogContent-root"], .no-drag'}
      disabled={isMaximized}
      nodeRef={nodeRef}
    >
      <Paper {...other} ref={nodeRef} />
    </Draggable>
  );
}

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const TOOLTIP_DESVINCULACION =
  'Adjunte la comunicación formal dirigida a la Unidad de Registro de Usuarios, la cual no requiere la exposición de motivos, pero sí debe estar firmada o sellada por el solicitante.';

/** Estados cuyo checkbox de selección no debe habilitarse (tienen una solicitud/estado que ya bloquea acciones masivas) */
const NON_SELECTABLE_STATUSES = [
  BuzonNucStatusEnum.DESHABILITADO,
  BuzonNucStatusEnum.PENDIENTE_FIRMA,
  BuzonNucStatusEnum.PENDIENTE_DESHABILITAR,
  BuzonNucStatusEnum.PENDIENTE_REHABILITAR,
];

const BuzonJudicial = () => {
  const { t } = useTranslation();
  const {
    state,
    activeNucsCount,
    activateNuc,
    activateSelected,
    activateConInstancia,
    invalidateBuzonQueries,
    deactivateNuc,
    deactivateSelected,
    reactivateNucs,
    cancelSolicitud,
    getAllNucIds,
  } = useBuzonJudicial();
  useBuzonRealtimeUpdates();
  const habilitar = useHabilitarBuzonJudicial({ activateNuc, activateSelected, activateConInstancia, invalidateBuzonQueries });
  const { userData } = useUserData();
  const { isAdministrador, isEntidad, isMinisterioPublico } = useUserPermissions();
  const idUsuario = userData?.id || 0;
  const perfil: PerfilEntidad | null = storage.get(StorageKeys.PERFIL);
  const idRelUsuarioEntidades = perfil?.infoEntidad?.relacion?.idRelUsuarioEntidades || null;

  const [searchTerm, setSearchTerm] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [modalState, setModalState] = useState<DeactivateModalState>({
    open: false,
    mode: 'single',
    action: 'deactivate',
  });
  const [isDeactivateModalMaximized, setIsDeactivateModalMaximized] = useState(false);
  const [documentoDesvinculacion, setDocumentoDesvinculacion] = useState<File | null>(null);
  const [comentario, setComentario] = useState('');
  /** Modo elegido al rehabilitar un caso DESHABILITADO — mismo concepto que en Habilitar (punto 4) */
  const [modoRehabilitar, setModoRehabilitar] = useState<ModoHabilitacion>('NUC');
  /** Categorías seleccionadas dentro del modal compartido de deshabilitar/rehabilitar: al deshabilitar
   *  representan las instancias A DESHABILITAR (punto 3, default = todas autorizadas); al rehabilitar por
   *  instancia representan las instancias A REHABILITAR (punto 4). */
  const [categoriasSeleccionadasModal, setCategoriasSeleccionadasModal] = useState<BuzonCategoriaTribunal[]>([]);
  const [tableScrollHeight, setTableScrollHeight] = useState(250);
  const [globalSelection, setGlobalSelection] = useState<Map<number, BuzonNucPreference>>(new Map());
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const deactivateModalNodeRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { data: nucsPage, isLoading: isNucsPageLoading } = useNucsPage({ idUsuario, searchTerm, pageNumber, pageSize, idRelUsuarioEntidades });

  /** Instancias disponibles del caso que se está rehabilitando — solo se consulta cuando el modal de
   *  rehabilitación está abierto y el usuario eligió el modo "Instancia" (misma lógica que Habilitar). */
  const requiereInstanciasRehabilitar = modalState.open && modalState.action === 'reactivate' && modoRehabilitar === 'INSTANCIA';
  const idsCasosRehabilitar = useMemo(() => (modalState.idCaso ? [modalState.idCaso] : []), [modalState.idCaso]);
  const {
    data: instanciasRehabilitarData,
    isLoading: isLoadingInstanciasRehabilitar,
    isError: isErrorInstanciasRehabilitar,
    refetch: refetchInstanciasRehabilitar,
  } = useInstanciasDisponibles(idsCasosRehabilitar, requiereInstanciasRehabilitar);
  const opcionesRehabilitarPorInstancia = modalState.idCaso
    ? filtrarCategoriasPorCaso(modalState.idCaso, instanciasRehabilitarData?.detallePorCaso ?? [], instanciasRehabilitarData?.instanciasDisponibles ?? [])
    : [];

  /** Validación adicional del modal compartido: si se está deshabilitando/rehabilitando por instancia,
   *  exige al menos 1 categoría marcada (y que la consulta de instancias, si aplica, no esté cargando/con error). */
  const esDeshabilitarPorInstancia = modalState.action === 'deactivate' && modalState.categoriasAutorizadas !== undefined;
  const esRehabilitarPorInstancia = modalState.action === 'reactivate' && modoRehabilitar === 'INSTANCIA';
  const seleccionInstanciaModalValida =
    (!esDeshabilitarPorInstancia && !esRehabilitarPorInstancia) ||
    (categoriasSeleccionadasModal.length > 0 && (!esRehabilitarPorInstancia || (!isLoadingInstanciasRehabilitar && !isErrorInstanciasRehabilitar)));

  /** NUCs de la página actual cuyo checkbox es seleccionable */
  const pageSelectableRows = useMemo(
    () => (nucsPage?.data || []).filter((r) => !NON_SELECTABLE_STATUSES.includes(r.estado)),
    [nucsPage?.data],
  );
  /** La selección múltiple solo tiene sentido con 2+ NUCs seleccionables (con 1 se usa la acción individual de la fila) */
  const canSelectRows = pageSelectableRows.length > 1;
  /** Cuántos de los NUCs seleccionables de la página actual están marcados */
  const pageSelectedCount = useMemo(
    () => pageSelectableRows.filter((r) => globalSelection.has(r.idCaso)).length,
    [pageSelectableRows, globalSelection],
  );
  const isPageFullySelected = canSelectRows && pageSelectedCount === pageSelectableRows.length;
  const isPageIndeterminate = canSelectRows && pageSelectedCount > 0 && !isPageFullySelected;

  // ── Derivar selección por estado (desde Map global) ──────────
  const selectedArray = useMemo(() => Array.from(globalSelection.values()), [globalSelection]);

  const selectedActivables = useMemo(
    () => selectedArray.filter((r) => [BuzonNucStatusEnum.NO_HABILITADO, BuzonNucStatusEnum.DESHABILITADO, BuzonNucStatusEnum.RECHAZADO].includes(r.estado)),
    [selectedArray],
  );
  const selectedHabilitados = useMemo(
    () => selectedArray.filter((r) => r.estado === BuzonNucStatusEnum.HABILITADO),
    [selectedArray],
  );
  const selectedPendientesDeshabilitar = useMemo(
    () => selectedArray.filter((r) => r.estado === BuzonNucStatusEnum.PENDIENTE_DESHABILITAR),
    [selectedArray],
  );

  // Define si las funcionalidades/elementos deben MOSTRARSE u OCULTARSE en la UI.
  const showBotonHabilitarMasivo = !isEntidad || isAdministrador || isMinisterioPublico;
  const showBotonDeshabilitarMasivo = !isEntidad || isAdministrador;
  const showCheckboxesSeleccion = showBotonHabilitarMasivo || showBotonDeshabilitarMasivo;

  /** Se pueden activar/habilitar si SOLO hay activables seleccionados (>1) */
  const canActivateSelected = selectedActivables.length > 1 && selectedHabilitados.length === 0 && selectedPendientesDeshabilitar.length === 0;
  /** Tipos de habilitación presentes entre los HABILITADO seleccionados ('NUC' | 'INSTANCIA') */
  const tiposHabilitacionSeleccionados = useMemo(
    () => new Set(selectedHabilitados.map((r) => (r.tipoHabilitacion === 2 ? 'INSTANCIA' : 'NUC'))),
    [selectedHabilitados],
  );
  /** Se pueden inactivar/deshabilitar si SOLO hay habilitados seleccionados (>1) y todos comparten el
   *  mismo tipo de habilitación — mezclar un caso habilitado por caso con uno por instancia en un mismo
   *  lote no está soportado, cada tipo requeriría un flujo de deshabilitación distinto. */
  const canDeactivateSelected = selectedHabilitados.length > 1 && selectedActivables.length === 0 && tiposHabilitacionSeleccionados.size <= 1;

  /** Selecciona/deselecciona un NUC individual */
  const handleToggleRow = useCallback((row: BuzonNucPreference) => {
    if (!showCheckboxesSeleccion) return;
    setGlobalSelection((prev) => {
      const next = new Map(prev);
      if (next.has(row.idCaso)) {
        next.delete(row.idCaso);
      } else {
        next.set(row.idCaso, row);
      }
      return next;
    });
  }, [showCheckboxesSeleccion]);

  /** Selecciona/deselecciona todos los NUCs seleccionables de la página actual */
  const handleToggleSelectAllPage = useCallback((checked: boolean) => {
    if (!showCheckboxesSeleccion) return;
    setGlobalSelection((prev) => {
      const next = new Map(prev);
      for (const row of pageSelectableRows) {
        if (checked) {
          next.set(row.idCaso, row);
        } else {
          next.delete(row.idCaso);
        }
      }
      return next;
    });
  }, [showCheckboxesSeleccion, pageSelectableRows]);

  /** Seleccionar todos los NUCs (inter-pagina) */
  const handleSelectAll = useCallback(async () => {
    const allIds = await getAllNucIds(searchTerm || undefined);
    setGlobalSelection((prev) => {
      const next = new Map(prev);
      // Map IdEstado numérico a BuzonNucStatus string (NULL = No habilitado)
      const estadoMap: Record<number, BuzonNucStatusEnum> = {
        172: BuzonNucStatusEnum.PENDIENTE_FIRMA,
        173: BuzonNucStatusEnum.HABILITADO,
        174: BuzonNucStatusEnum.DESHABILITADO,
        177: BuzonNucStatusEnum.PENDIENTE_DESHABILITAR,
        178: BuzonNucStatusEnum.PENDIENTE_REHABILITAR,
      };
      for (const item of allIds) {
        const estado = estadoMap[item.idEstado] ?? BuzonNucStatusEnum.NO_HABILITADO;
        if (estado !== BuzonNucStatusEnum.DESHABILITADO && estado !== BuzonNucStatusEnum.PENDIENTE_FIRMA) {
          next.set(item.idCaso, { idCasoBuzon: item.idCasoBuzon, idCaso: item.idCaso, nuc: '', creadoEn: '', referencia: '', estado });
        }
      }
      
      return next;
    });
  }, [getAllNucIds, searchTerm]);

  useEffect(() => {
    const container = tableContainerRef.current;

    if (!container) {
      return;
    }

    const updateTableHeight = () => {
      const containerHeight = container.clientHeight;
      const reservedSpace = 90;
      const nextHeight = Math.max(containerHeight - reservedSpace, 160);

      setTableScrollHeight((previous) => (previous === nextHeight ? previous : nextHeight));
    };

    updateTableHeight();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateTableHeight();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // ── Helpers de notificación ───────────────────────────────────

  const showAuthorizationEmailSent = (subject: string) => {
    sileo.success({
      title: t('buzonJudicial.messages.authorizationRequestSent', 'Solicitud enviada para firma'),
      description: t('buzonJudicial.messages.authorizationRequestSentDetail', {
        subject,
        defaultValue: `Se envió un correo para firma digital. Asunto: ${subject}`,
      }),
      autopilot: {
        expand: 500,
        collapse: 3000,
      },
    });
  };

  const showInactivationSuccess = () => {
    sileo.success({
      title: t('buzonJudicial.messages.inactivationSent', 'Solicitud de deshabilitación registrada'),
      description: t(
        'buzonJudicial.messages.inactivationSentDetail',
        'La solicitud fue registrada con su documento de desvinculación.',
      ),
      autopilot: {
        expand: 500,
        collapse: 3000,
      },
    });
  };

  // ── Acciones individuales ─────────────────────────────────────

  const handleCancelSolicitud = (row: BuzonNucPreference) => {
    const mensajePorEstado: Partial<Record<BuzonNucStatusEnum, string>> = {
      [BuzonNucStatusEnum.PENDIENTE_FIRMA]: t(
        'buzonJudicial.messages.cancelConfirmPendienteFirma',
        '¿Desea cancelar la solicitud de firma pendiente?',
      ),
      [BuzonNucStatusEnum.PENDIENTE_REHABILITAR]: t(
        'buzonJudicial.messages.cancelConfirmPendienteHabilitar',
        '¿Desea cancelar la solicitud de habilitación pendiente?',
      ),
      [BuzonNucStatusEnum.PENDIENTE_DESHABILITAR]: t(
        'buzonJudicial.messages.cancelConfirmPendienteDeshabilitar',
        '¿Desea cancelar la solicitud de deshabilitación pendiente?',
      ),
    };

    const mensaje = mensajePorEstado[row.estado] ?? t(
      'buzonJudicial.messages.cancelConfirmGeneric',
      '¿Desea cancelar la solicitud pendiente?',
    );

    customAlert({
      titulo: t('buzonJudicial.messages.cancelConfirmTitle', 'Confirmar cancelación'),
      mensaje,
      icon: 'question',
      botonCancelar: true,
      textoBotonAceptar: t('actions.confirm', 'Confirmar'),
      textoBotonCancelar: t('actions.cancel', 'Cancelar'),
      cerrarClicFuera: false,
      cerrarEsc: false,
      callback: async (isConfirmed) => {
        if (!isConfirmed) return;
        setActionLoadingId(row.idCasoBuzon);
        try {
          await cancelSolicitud([row.idCasoBuzon]);
          sileo.success({
            title: t('buzonJudicial.messages.solicitudCancelada', 'Solicitud cancelada exitosamente.'),
          });
        } catch {
          sileo.error({
            title: t('buzonJudicial.messages.solicitudCancelacionFailed', 'Error al cancelar la solicitud.'),
          });
        } finally {
          setActionLoadingId(null);
        }
      },
    });
  };

  //Acciones (seleccionados)

  const handleActivateSelectedNucs = () => {
    habilitar.abrirModal(selectedActivables, 'estandar');
  };

  /** Compone los toasts a partir del resumen agregado que retorna el modal Habilitar (uno o varios
   *  grupos, según cuántas instancias distintas se hayan asignado — ver useHabilitarBuzonJudicial). */
  const handleHabilitarConfirmado = (resumen: ResumenActivacionAgrupada) => {
    const { exitosos, fallidos } = resumen;
    const total = exitosos.length + fallidos.length;

    if (total > 0 && fallidos.length === total) {
      sileo.error({
        title: t('buzonJudicial.messages.activationFailed', 'Error al enviar la solicitud de habilitación.'),
      });
      return;
    }

    if (exitosos.length === 1) {
      showAuthorizationEmailSent(exitosos[0].result.subject);
    } else if (exitosos.length > 1) {
      sileo.success({
        title: t('buzonJudicial.enableModal.successMultipleGroups', {
          count: exitosos.length,
          defaultValue: `Se enviaron ${exitosos.length} solicitudes de habilitación por instancia`,
        }),
        autopilot: { expand: 500, collapse: 3000 },
      });
    }

    if (fallidos.length > 0) {
      sileo.error({
        title: t('buzonJudicial.enableModal.partialFailureDetail', {
          succeeded: exitosos.length,
          total,
          failed: fallidos.length,
          defaultValue: `${exitosos.length} de ${total} solicitudes enviadas. ${fallidos.length} fallaron.`,
        }),
      });
    }

    if (exitosos.length > 0) {
      setGlobalSelection(new Map());
    }
  };

  // ── Modal de desactivación ────────────────────────────────────

  const openSingleDeactivateModal = (row: BuzonNucPreference) => {
    customAlert({
      titulo: t('buzonJudicial.messages.reactivateConfirmTitle', 'Advertencia'),
      mensaje: t(
        'buzonJudicial.messages.reactivateConfirmDescription',
        'Las notificaciones realizadas durante el trámite de la desafiliación serán válidas',
      ),
      icon: 'warning',
      botonCancelar: true,
      textoBotonAceptar: t('buzonJudicial.actions.agree', 'De acuerdo'),
      textoBotonCancelar: t('actions.cancel', 'Cancelar'),
      cerrarClicFuera: false,
      cerrarEsc: false,
      callback: (isConfirmed) => {
        if (!isConfirmed) return;
        setDocumentoDesvinculacion(null);
        setComentario('');
        setIsDeactivateModalMaximized(false);
        const esPorInstancia = row.estado === BuzonNucStatusEnum.HABILITADO && row.tipoHabilitacion === 2;
        const categoriasAutorizadas = row.categoriasAutorizadas ?? [];
        // Por defecto se preseleccionan TODAS las categorías autorizadas — equivale a deshabilitar el
        // caso completo, mismo resultado que el flujo actual, salvo que el usuario deselecciona alguna.
        setCategoriasSeleccionadasModal(esPorInstancia ? categoriasAutorizadas : []);
        setModalState({
          open: true,
          mode: 'single',
          action: 'deactivate',
          idCasoBuzon: row.idCasoBuzon,
          idCaso: row.idCaso,
          nucLabel: row.nuc,
          categoriasAutorizadas: esPorInstancia ? categoriasAutorizadas : undefined,
        });
      },
    });
  };

  const openMultipleDeactivateModal = () => {
    customAlert({
      titulo: t('buzonJudicial.messages.reactivateConfirmTitle', 'Advertencia'),
      mensaje: t(
        'buzonJudicial.messages.reactivateConfirmDescription',
        'Las notificaciones realizadas durante el trámite de la desafiliación serán válidas.',
      ),
      icon: 'warning',
      botonCancelar: true,
      textoBotonAceptar: t('buzonJudicial.actions.agree', 'De acuerdo'),
      textoBotonCancelar: t('actions.cancel', 'Cancelar'),
      cerrarClicFuera: false,
      cerrarEsc: false,
      callback: (isConfirmed) => {
        if (!isConfirmed) return;
        setDocumentoDesvinculacion(null);
        setComentario('');
        setIsDeactivateModalMaximized(false);
        // La deshabilitación masiva siempre deshabilita el/los caso(s) completo(s) — elegir instancias
        // específicas queda restringido a la acción individual de fila (mismo criterio que "habilitar").
        setCategoriasSeleccionadasModal([]);
        setModalState({
          open: true,
          mode: 'multiple',
          action: 'deactivate',
          idsCasoBuzon: selectedHabilitados.map((r) => r.idCasoBuzon)
        });
      },
    });
  };

  const openReactivateModal = (row: BuzonNucPreference) => {
    setDocumentoDesvinculacion(null);
    setComentario('');
    setIsDeactivateModalMaximized(false);
    setModoRehabilitar('NUC');
    setCategoriasSeleccionadasModal([]);
    setModalState({
      open: true,
      mode: 'single',
      action: 'reactivate',
      idCasoBuzon: row.idCasoBuzon,
      idCaso: row.idCaso,
      nucLabel: row.nuc,
    });
  };

  const closeDeactivateModal = () => {
    setIsDeactivateModalMaximized(false);
    setModalState((prev) => ({ ...prev, open: false }));
  };

  // ── Drag & Drop handlers (modal desactivación) ──────────────
  const validateFile = useCallback((file: File): boolean => {
    if (file.type !== 'application/pdf') {
      customAlert({
        titulo: t('buzonJudicial.modal.invalidFileTypeTitle', 'Formato no válido'),
        mensaje: t('buzonJudicial.modal.invalidFileTypeDetail', 'Solo se aceptan archivos en formato PDF.'),
        icon: 'warning',
        botonCancelar: false,
        textoBotonAceptar: t('actions.accept', 'Aceptar'),
      });
      return false;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      customAlert({
        titulo: t('buzonJudicial.modal.fileTooLargeTitle', 'Archivo demasiado grande'),
        mensaje: t('buzonJudicial.modal.fileTooLargeDetail', {
          size: MAX_FILE_SIZE_MB,
          defaultValue: `El archivo supera el tamaño máximo permitido de ${MAX_FILE_SIZE_MB} MB.`,
        }),
        icon: 'warning',
        botonCancelar: false,
        textoBotonAceptar: t('actions.accept', 'Aceptar'),
      });
      return false;
    }
    return true;
  }, [t]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    if (!file || !validateFile(file)) return;
    setDocumentoDesvinculacion(file);
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file || !validateFile(file)) return;
    setDocumentoDesvinculacion(file);
  }, [validateFile]);

  const handleDeleteFile = useCallback(() => {
    setDocumentoDesvinculacion(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleViewFile = useCallback(() => {
    if (!documentoDesvinculacion) return;
    const url = URL.createObjectURL(documentoDesvinculacion);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }, [documentoDesvinculacion]);

  const toggleDeactivateModalMaximize = () => {
    setIsDeactivateModalMaximized((prev) => !prev);
  };

  const handleConfirmDeactivate = async () => {
    if (!documentoDesvinculacion) {
      return;
    }

    setIsModalLoading(true);
    try {
      const base64 = await fileToBase64(documentoDesvinculacion, false);
      const idsCategoriaTribunalSeleccionadas = categoriasSeleccionadasModal.map((c) => c.idCategoriaTribunal);

      if (modalState.action === 'reactivate') {
        const idsCasoBuzon = modalState.mode === 'single' && modalState.idCasoBuzon
          ? [modalState.idCasoBuzon]
          : modalState.idsCasoBuzon ?? [];
        const payload: BuzonReactivatePayload = {
          documentoBase64: base64,
          nombreDocumento: documentoDesvinculacion.name,
          comentario,
          // Si el usuario eligió "Instancia", rehabilita solo las categorías marcadas (punto 4).
          // Si eligió "NUC" (default), no se manda tipoHabilitacion/idsCategoriaTribunal — comportamiento actual, sin cambios.
          ...(modoRehabilitar === 'INSTANCIA'
            ? { tipoHabilitacion: 2 as const, idsCategoriaTribunal: idsCategoriaTribunalSeleccionadas }
            : {}),
        };
        const result = await reactivateNucs(idsCasoBuzon, payload);
        closeDeactivateModal();
        sileo.success({
          title: t('buzonJudicial.messages.reactivationRequestSent', 'Solicitud registrada'),
          description: t('buzonJudicial.messages.reactivationRequestSentDetail', {
            idSolicitud: result.idSolicitud,
            defaultValue: `Su solicitud #${result.idSolicitud} ha sido registrada. Será atendida en un plazo máximo de 72 horas.`,
          }),
          autopilot: { expand: 500, collapse: 5000 },
        });
      } else {
        const payload: BuzonDeactivatePayload = {
          documentoBase64: base64,
          nombreDocumento: documentoDesvinculacion.name,
          comentario,
          // Solo se manda cuando el caso es Habilitado por instancia (punto 3) — deshabilita ÚNICAMENTE
          // las categorías marcadas. Si es undefined (caso por NUC o deshabilitación masiva), el backend
          // deshabilita el caso completo, igual que siempre.
          ...(modalState.categoriasAutorizadas !== undefined
            ? { idsCategoriaTribunal: idsCategoriaTribunalSeleccionadas }
            : {}),
        };
        if (modalState.mode === 'single' && modalState.idCasoBuzon) {
          await deactivateNuc(modalState.idCasoBuzon, payload);
        } else if (modalState.idsCasoBuzon && modalState.idsCasoBuzon.length > 0) {
          await deactivateSelected(modalState.idsCasoBuzon, payload);
        }
        closeDeactivateModal();
        showInactivationSuccess();
      }

      setGlobalSelection(new Map());
    } catch {
      sileo.error({
        title: modalState.action === 'reactivate'
          ? t('buzonJudicial.messages.reactivationFailed', 'Error al registrar la solicitud de habilitación.')
          : t('buzonJudicial.messages.deactivationFailed', 'Error al registrar la solicitud de deshabilitación.'),
      });
    } finally {
      setIsModalLoading(false);
    }
  };
  // ── Columnas de la tabla ──────────────────────────────────────

  const columns: TableColumn<BuzonNucPreference>[] = [
    {
      name: (
        <Tooltip title={t('buzonJudicial.actions.selectAll', 'Seleccionar todos')} arrow placement='right'>
          <Checkbox
            size="small"
            checked={isPageFullySelected}
            indeterminate={isPageIndeterminate}
            disabled={!canSelectRows}
            hidden={!showCheckboxesSeleccion}
            onChange={(e) => handleToggleSelectAllPage(e.target.checked)}
            slotProps={{ input: { 'aria-label': t('buzonJudicial.actions.selectAll', 'Seleccionar todos') } }}
          />
        </Tooltip>
      ),
      width: '56px',
      center: true,
      cell: (row) => (
        <Tooltip title={t('buzonJudicial.columns.select', 'Seleccionar NUC')} arrow placement='right'>
          <Checkbox
            size="small"
            checked={globalSelection.has(row.idCaso)}
            disabled={!canSelectRows || NON_SELECTABLE_STATUSES.includes(row.estado)}
            hidden={!showCheckboxesSeleccion}
            onChange={() => handleToggleRow(row)}
            slotProps={{ input: { 'aria-label': t('buzonJudicial.columns.select', 'Seleccionar NUC') } }}
          />
        </Tooltip>
      ),
    },
    {
      name: t('buzonJudicial.columns.nuc', 'NUC'),
      selector: (row) => row.nuc,
      sortable: true,
      minWidth: '130px',
      grow: 1,
      center: true,
      cell: (row) => (
        <Tooltip title={row.nuc} arrow>
          <span className="block w-full text-center truncate">{row.nuc}</span>
        </Tooltip>
      ),
    },
    {
      name: t('buzonJudicial.columns.createdAt', 'Fecha Activado'),
      selector: (row) => row.creadoEn ? format(parseISO(row.creadoEn), 'dd/MM/yyyy', { locale: es }) : '',
      sortable: true,
      minWidth: '120px',
      grow: 1,
      center: true,
    },
    {
      name: t('buzonJudicial.columns.reference', 'Referencia'),
      selector: (row) => row.referencia,
      minWidth: '150px',
      grow: 2,
      center: true,
      cell: (row) => (
        <Tooltip title={row.referencia} arrow>
          <span className="block w-full text-center truncate">{row.referencia}</span>
        </Tooltip>
      ),
    },
    {
      name: t('buzonJudicial.columns.status', 'Estado'),
      selector: (row) => row.estado,
      minWidth: '200px',
      grow: 1,
      center: true,
      cell: (row) => (
        <div className="flex justify-center w-full">
          <BuzonEstadoChip row={row} />
        </div>
      ),
    },
    {
      name: t('buzonJudicial.columns.actions', 'Acciones'),
      minWidth: '140px',
      grow: 1,
      center: true,
      cell: (row) => (
        <BuzonAccionesCell
          row={row}
          isRowLoading={actionLoadingId === row.idCasoBuzon}
          isDisabled={actionLoadingId !== null || globalSelection.size > 1}
          tooltipOverride={
            globalSelection.size > 1
              ? t('buzonJudicial.tooltips.rowActionsDisabledMultiple', 'Deseleccione para usar las acciones individuales de cada NUC')
              : undefined
          }
          onDeactivate={openSingleDeactivateModal}
          onCancelSolicitud={handleCancelSolicitud}
          onActivate={(r) => habilitar.abrirModal([r], 'estandar')}
          onReactivate={openReactivateModal}
          onActivateNuevaInstancia={(r) => habilitar.abrirModal([r], 'nueva')}
        />
      ),
    },
  ];

  return (
    <div className="h-full min-h-0 w-full flex flex-col gap-3">
      {/* ── Backdrop global: bloquea toda la interfaz durante acciones ── */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 10,
          position: 'fixed',
        }}
        open={actionLoadingId !== null}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={60} thickness={4} />
          <Typography variant="h6" component="p">
            {t('buzonJudicial.messages.processingRequest', 'Procesando solicitud, por favor espere...')}
          </Typography>
        </Box>
      </Backdrop>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <SearchInputMUI
              placeholder={t('buzonJudicial.searchByNuc', 'Buscar por NUC')}
              width="100%"
              onSearch={(value) => {
                setSearchTerm(value);
                setPageNumber(1);
              }}
              title={t('actions.search', 'Buscar')}
            />
          </div>
          <BuzonAyudaPopover maxFileSizeMb={MAX_FILE_SIZE_MB} />
        </div>

        <div className="flex flex-col md:flex-row gap-2 md:justify-end shrink-0 w-full md:w-auto items-center">
          {/* <Button
            size="small"
            variant="text"
            disabled
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', fontSize: '0.85rem', display: 'none' }}
          >
            {t('buzonJudicial.actions.selectAll', 'Seleccionar todos')}
          </Button> */}
          {showBotonHabilitarMasivo && (
            <Tooltip
              title={
                canActivateSelected
                  ? t('buzonJudicial.actions.enableSelectedPendingNucs', 'Habilitar los NUCs pendientes seleccionados')
                  : t('buzonJudicial.messages.selectOneOrMorePendingNucsToEnable', 'Seleccione uno o más NUCs pendientes para habilitar.')
              }
              arrow
            >
              <span className="block w-full md:w-auto">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RiCheckFill size={16} />}
                  onClick={handleActivateSelectedNucs}
                  disabled={!canActivateSelected || actionLoadingId !== null}
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    justifyContent: 'center',
                    color: '#22c55e',
                    borderColor: '#22c55e',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    px: 2.5,
                    transition: 'all 0.2s ease',
                    '&:hover:not(:disabled)': {
                      backgroundColor: '#f0fdf4',
                      borderColor: '#22c55e',
                    },
                    '&:disabled': {
                      color: '#d1d5db',
                      borderColor: '#e5e7eb',
                    },
                  }}
                >
                  {t('buzonJudicial.actions.enableSelected', 'Habilitar seleccionados')}
                </Button>
              </span>
            </Tooltip>
          )}
          {showBotonDeshabilitarMasivo && (
            <Tooltip
              title={
                canDeactivateSelected
                  ? t('buzonJudicial.actions.deactivateSelectedTooltip', 'Deshabilitar los NUCs habilitados seleccionados')
                  : selectedHabilitados.length > 1 && tiposHabilitacionSeleccionados.size > 1
                    ? t(
                        'buzonJudicial.messages.cannotMixHabilitationTypes',
                        'No se pueden deshabilitar en un mismo lote NUCs habilitados por caso junto con NUCs habilitados por instancia.',
                      )
                    : t(
                        'buzonJudicial.messages.selectMoreNucsToDisable',
                        'Seleccione más de un NUC para deshabilitar.',
                      )
              }
              arrow
            >
              <span className="block w-full md:w-auto">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RiCloseLine size={16} />}
                  onClick={openMultipleDeactivateModal}
                  disabled={!canDeactivateSelected || actionLoadingId !== null}
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    justifyContent: 'center',
                    color: '#D32727',//'#9ca3af',
                    borderColor: '#d1d5db',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    px: 2.5,
                    transition: 'all 0.2s ease',
                    '&:hover:not(:disabled)': {
                      backgroundColor: '#f3f4f6',
                      borderColor: '#D32727',//'#9ca3af',
                    },
                    '&:disabled': {
                      color: '#d1d5db',
                      borderColor: '#e5e7eb',
                    },
                  }}
                >
                  {t('buzonJudicial.actions.disableSelected', 'Deshabilitar seleccionados')}
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      <Box className="flex justify-between items-center text-xs text-gray-500">
        <span>
          {state.autoActivarTodoNuc
            ? t(
                'buzonJudicial.messages.autoActivationEnabled',
                'Autorizacion general habilitada: los NUCs futuros se habilitaran automaticamente.',
              )
            : null
          }
        </span>
        {globalSelection.size > 0 && (
          <span className="whitespace-nowrap">{globalSelection.size} seleccionado(s)</span>
        )}
      </Box>

      <div ref={tableContainerRef} className="bg-white border border-gray-200 rounded-lg flex-1 min-h-0 w-full overflow-hidden">
        <Box sx={{ height: '100%', minHeight: 0 }}>
          <DataTable
            keyField="idCaso"
            columns={columns}
            data={nucsPage?.data || []}
            responsive
            pagination
            paginationServer
            paginationTotalRows={nucsPage?.rowCount || 0}
            paginationPerPage={pageSize}
            paginationRowsPerPageOptions={LISTA_OPCIONES_PAGINACION}
            onChangePage={(newPage) => setPageNumber(newPage)}
            onChangeRowsPerPage={(newPageSize) => {
              setPageSize(newPageSize);
              setPageNumber(1);
            }}
            progressPending={isNucsPageLoading}
            progressComponent={
              <div className="w-full px-4 py-2 space-y-2">
                {Array.from({ length: pageSize }).map((_, i) => (
                  <div key={i} className="flex gap-3 items-center">
                    <Skeleton variant="text" width="20%" height={40} />
                    <Skeleton variant="text" width="18%" height={40} />
                    <Skeleton variant="text" width="26%" height={40} />
                    <Skeleton variant="text" width="18%" height={40} />
                    <Skeleton variant="rounded" width="18%" height={32} />
                  </div>
                ))}
              </div>
            }
            noDataComponent={
              <TableEmptyState
                title={t('buzonJudicial.messages.noNucs', 'No hay NUCs disponibles')}
                text={
                  searchTerm ? (
                    t(
                      'buzonJudicial.messages.noNucsDetail',
                      'No se encontraron NUCs con el filtro aplicado.',
                    )
                  )
                  : (
                    t(
                      'buzonJudicial.messages.noNucsFoundToEnableJudicialMailbox',
                      'No se encontraron NUCs para habilitar el Buzón Judicial.',
                    )
                  )
                }
                buttonText=""
              />
            }
            customStyles={{
              ...CUSTOM_STYLES,
              table: {
                style: {
                  width: '100%',
                },
              },
              rows: {
                style: {
                  minHeight: '56px',
                  fontSize: '0.875rem',
                  width: '100%',
                },
              },
              tableWrapper: {
                style: {
                  width: '100%',
                  display: 'block',
                },
              },
              responsiveWrapper: {
                style: {
                  width: '100%',
                  display: 'block',
                },
              },
              headCells: {
                style: {
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px',
                },
              },
              cells: {
                style: {
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px',
                },
              },
              pagination: {
                style: {
                  justifyContent: 'flex-end',
                  paddingRight: '20px',
                },
              },
            }}
            fixedHeader
            fixedHeaderScrollHeight={`${tableScrollHeight}px`}
            paginationComponentOptions={{
              rowsPerPageText: t('casos.rowsPerPage', 'Filas por pagina:'),
              rangeSeparatorText: t('casos.of', 'de'),
              selectAllRowsItem: false,
            }}
          />
        </Box>
      </div>

      {/* ── Modal de Inactivación ────────────────────────────────── */}
      <Dialog
        open={modalState.open}
        onClose={isModalLoading ? undefined : closeDeactivateModal}
        maxWidth={isDeactivateModalMaximized ? false : 'sm'}
        fullWidth={!isDeactivateModalMaximized}
        fullScreen={isDeactivateModalMaximized}
        PaperComponent={DraggablePaperComponent}
        PaperProps={{
          isMaximized: isDeactivateModalMaximized,
          nodeRef: deactivateModalNodeRef,
          sx: {
            borderRadius: isDeactivateModalMaximized ? 0 : 2,
            ...(isDeactivateModalMaximized && {
              m: 0,
              width: '100vw',
              height: '100vh',
              maxWidth: '100vw',
              maxHeight: '100vh',
            }),
          },
        } as PaperProps}
        aria-labelledby="draggable-dialog-inactivar-title"
      >
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.modal + 10,
            position: 'fixed',
          }}
          open={isModalLoading}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress color="inherit" size={60} thickness={4} />
            <Typography variant="h6" component="p">
              {t('buzonJudicial.messages.processingRequest', 'Procesando solicitud, por favor espere...')}
            </Typography>
          </Box>
        </Backdrop>
        <DialogTitle
          id="draggable-dialog-inactivar-title"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: isDeactivateModalMaximized ? 'default' : 'move',
            userSelect: 'none',
          }}
        >
          {modalState.action === 'reactivate'
            ? t('buzonJudicial.modal.reactivateTitle', 'Solicitud de Habilitación')
            : modalState.mode === 'single'
              ? t('buzonJudicial.modal.singleTitle', 'Deshabilitar NUC')
              : t('buzonJudicial.modal.disableSelectedNucs', 'Deshabilitar NUCs seleccionados')}
          <Box className="flex items-center gap-1 no-drag">
            <IconButton onClick={toggleDeactivateModalMaximize} size="small">
              {isDeactivateModalMaximized ? <RiFullscreenExitLine size={18} /> : <RiFullscreenLine size={18} />}
            </IconButton>
            <IconButton onClick={closeDeactivateModal} size="small">
              <RiCloseLine size={18} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-4 pt-2">
            <TextField
              label={t('buzonJudicial.modal.nuc', 'NUC')}
              value={
                modalState.mode === 'single'
                  ? modalState.nucLabel || ''
                  : t('buzonJudicial.modal.selectedNucs', {
                      count: modalState.idsCasoBuzon?.length || 0,
                      defaultValue: `${modalState.idsCasoBuzon?.length || 0} NUC(s) seleccionados`,
                    })
              }
              size="small"
              InputProps={{ readOnly: true }}
            />

            {/* Punto 3: deshabilitar solo algunas instancias de un caso Habilitado por instancia */}
            {modalState.action === 'deactivate' && modalState.categoriasAutorizadas !== undefined && (
              <Box>
                <CategoriaTribunalSelect
                  label={t('buzonJudicial.modal.categoriasADeshabilitarLabel', 'Instancias a deshabilitar')}
                  options={modalState.categoriasAutorizadas}
                  value={categoriasSeleccionadasModal}
                  onChange={setCategoriasSeleccionadasModal}
                  disabled={isModalLoading}
                />
                {categoriasSeleccionadasModal.length === 0 && (
                  <Typography sx={{ fontSize: '0.75rem', color: '#ef4444', mt: 0.5 }}>
                    {t('buzonJudicial.modal.mustSelectAtLeastOneCategoryToDisable', 'Selecciona al menos una instancia a deshabilitar.')}
                  </Typography>
                )}
                {categoriasSeleccionadasModal.length > 0 && categoriasSeleccionadasModal.length < modalState.categoriasAutorizadas.length && (
                  <Typography sx={{ fontSize: '0.75rem', color: '#b45309', mt: 0.5 }}>
                    {t('buzonJudicial.modal.partialDeactivationHint', 'El caso seguirá habilitado con las instancias no seleccionadas.')}
                  </Typography>
                )}
              </Box>
            )}

            {/* Punto 4: rehabilitar por NUC (comportamiento actual) o por instancia — misma lógica que Habilitar */}
            {modalState.action === 'reactivate' && (
              <Box>
                <HabilitarModoToggle value={modoRehabilitar} onChange={setModoRehabilitar} />

                {modoRehabilitar === 'INSTANCIA' && (
                  <Box sx={{ mt: 1.5 }}>
                    {isLoadingInstanciasRehabilitar && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={18} />
                        <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
                          {t('buzonJudicial.enableModal.loadingInstancias', 'Cargando instancias disponibles...')}
                        </Typography>
                      </Box>
                    )}
                    {!isLoadingInstanciasRehabilitar && isErrorInstanciasRehabilitar && (
                      <Alert
                        severity="error"
                        action={
                          <Button color="inherit" size="small" onClick={() => refetchInstanciasRehabilitar()}>
                            {t('buzonJudicial.enableModal.retry', 'Reintentar')}
                          </Button>
                        }
                      >
                        {t('buzonJudicial.enableModal.errorLoadingInstancias', 'No se pudieron cargar las instancias disponibles')}
                      </Alert>
                    )}
                    {!isLoadingInstanciasRehabilitar && !isErrorInstanciasRehabilitar && (
                      <>
                        <CategoriaTribunalSelect
                          options={opcionesRehabilitarPorInstancia}
                          value={categoriasSeleccionadasModal}
                          onChange={setCategoriasSeleccionadasModal}
                          disabled={isModalLoading || opcionesRehabilitarPorInstancia.length === 0}
                        />
                        {opcionesRehabilitarPorInstancia.length === 0 && (
                          <Typography sx={{ fontSize: '0.75rem', color: '#b45309', mt: 0.75 }}>
                            {t('buzonJudicial.enableModal.noNewCategoriesAvailable', 'No hay nuevas instancias disponibles para este caso.')}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}

            <div>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 0.2 }}>
                  <RiUploadLine size={15} />
                  {t('buzonJudicial.modal.unlinkDocument', 'Documento de justificación')}
                  <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                </Typography>
                <Tooltip
                  title={t('buzonJudicial.modal.unlinkDocumentTooltip', TOOLTIP_DESVINCULACION)}
                  arrow
                  placement="top"
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 320,
                        p: '10px 13px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, rgba(15,23,42,0.97) 0%, rgba(30,41,59,0.97) 100%)',
                        border: '1px solid rgba(148,163,184,0.25)',
                        fontSize: '0.78rem',
                        lineHeight: 1.6,
                      },
                    },
                  }}
                >
                  <Box component="span" sx={{ display: 'inline-flex', cursor: 'help', color: 'text.disabled', '&:hover': { color: 'text.secondary' }, transition: 'color 150ms' }}>
                    <RiInformationLine size={15} />
                  </Box>
                </Tooltip>
              </Box>

              {!documentoDesvinculacion && (
                <Box
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleFileDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    position: 'relative',
                    border: '1.5px dashed',
                    borderColor: isDragActive ? '#2563eb' : 'rgba(0,0,0,0.15)',
                    borderRadius: '12px',
                    p: 3,
                    textAlign: 'center',
                    background: isDragActive
                      ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
                      : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#94a3b8',
                      background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                    },
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    style={{ display: 'none' }}
                    onChange={handleFileInputChange}
                  />
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 1.5,
                      background: isDragActive
                        ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                        : 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                      boxShadow: isDragActive
                        ? '0 4px 14px rgba(37,99,235,0.35)'
                        : '0 4px 10px rgba(71,85,105,0.25)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <RiUploadCloudLine size={24} color="#fff" />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: isDragActive ? '#1d4ed8' : 'text.primary', mb: 0.25, transition: 'color 0.2s' }}>
                    {isDragActive
                      ? t('buzonJudicial.modal.dropFileHere', 'Suelte el archivo aquí')
                      : t('buzonJudicial.modal.dragFileOrClick', 'Arrastre el archivo o haga clic para seleccionar')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                    {t('buzonJudicial.modal.fileConstraints', { size: MAX_FILE_SIZE_MB, defaultValue: `Solo archivos PDF · máx. ${MAX_FILE_SIZE_MB} MB` })}
                  </Typography>
                </Box>
              )}

              {documentoDesvinculacion && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: '10px 14px',
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: 'rgba(34,197,94,0.3)',
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                }}>
                  <Box sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                    flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(22,163,74,0.3)',
                  }}>
                    <RiUploadLine size={16} color="#fff" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#15803d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {documentoDesvinculacion.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#16a34a' }}>
                      {(documentoDesvinculacion.size / (1024 * 1024)).toFixed(2)} MB
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                    <Tooltip title={t('actions.view', 'Ver archivo')} arrow>
                      <IconButton size="small" onClick={handleViewFile} sx={{ color: '#15803d', '&:hover': { background: 'rgba(22,163,74,0.12)' } }}>
                        <RiEyeLine size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('actions.delete', 'Eliminar archivo')} arrow>
                      <IconButton size="small" onClick={handleDeleteFile} sx={{ color: '#dc2626', '&:hover': { background: 'rgba(220,38,38,0.1)' } }}>
                        <RiDeleteBinLine size={16} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </div>

            <TextField
              label={t('buzonJudicial.modal.comment', 'Comentario')}
              multiline
              rows={3}
              value={comentario}
              onChange={(event) => setComentario(event.target.value)}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 1.75, display: 'flex', alignItems: 'center' }}>
          <Button onClick={closeDeactivateModal} color="inherit" disabled={isModalLoading}>
            {t('actions.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="contained"
            disabled={!documentoDesvinculacion || isModalLoading || !seleccionInstanciaModalValida}
            onClick={handleConfirmDeactivate}
            startIcon={isModalLoading ? <CircularProgress size={16} color="primary" /> : undefined}
          >
            {t('actions.confirm', 'Confirmar')}
          </Button>
        </DialogActions>
      </Dialog>

      <HabilitarBuzonModal habilitar={habilitar} onConfirmed={handleHabilitarConfirmado} />
    </div>
  );
};

export default BuzonJudicial;
